import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import Logo from "../components/Logo";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../hooks/use-toast";
import {
  ShieldCheck,
  LogOut,
  RefreshCw,
  Inbox,
  HandCoins,
  ChevronDown,
  ChevronRight,
  Download,
  Trash2,
  ShieldOff,
  AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminPage() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const [tab, setTab] = useState("demos");
  const [demos, setDemos] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [optouts, setOptouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, id, label }

  const loadAll = async () => {
    setBusy(true);
    try {
      const [d, a, o, s] = await Promise.all([
        axios.get(`${API}/demos`, { withCredentials: true }),
        axios.get(`${API}/affiliates`, { withCredentials: true }),
        axios.get(`${API}/optouts`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setDemos(d.data || []);
      setAffiliates(a.data || []);
      setOptouts(o.data || []);
      setStats(s.data || null);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) toast({ title: "Session expired", description: "Please sign in again." });
      else if (code === 403) toast({ title: "Not authorized", description: "Your email is not on the admin allowlist." });
      else toast({ title: "Error", description: "Could not load data." });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (location.state?.error) toast({ title: "Sign-in failed", description: location.state.error });
  }, [location.state, toast]);

  useEffect(() => {
    if (user) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const downloadCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/${kind}.csv`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const fname = `buyerbeacon-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.setAttribute("download", fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Export failed", description: "Could not download CSV." });
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { kind, id } = confirmDelete;
    try {
      await axios.delete(`${API}/${kind}/${id}`, { withCredentials: true });
      if (kind === "demos") setDemos((r) => r.filter((x) => x.id !== id));
      if (kind === "affiliates") setAffiliates((r) => r.filter((x) => x.id !== id));
      if (kind === "optouts") setOptouts((r) => r.filter((x) => x.id !== id));
      setStats((s) => s ? ({ ...s, [kind]: Math.max(0, (s[kind] || 1) - 1) }) : s);
      toast({ title: "Deleted", description: `Row removed.` });
    } catch {
      toast({ title: "Delete failed", description: "Could not delete row." });
    } finally {
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#1e1b4b]" />
          <p className="mt-4 text-sm text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/60 px-5">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_30px_80px_-30px_rgba(30,27,75,0.25)]">
          <div className="flex items-center justify-between">
            <Logo />
            <Link to="/" className="text-xs text-slate-500 hover:text-[#1e1b4b]">← Back to site</Link>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1e1b4b]/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1e1b4b]">
            <ShieldCheck className="h-3.5 w-3.5 text-[#d4af37]" />
            Admin Console
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-[#1e1b4b]">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Use the Google account on your admin allowlist.</p>

          <button
            onClick={loginWithGoogle}
            data-testid="admin-google-signin-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-[#1e1b4b] transition-all hover:border-[#1e1b4b]/40 hover:bg-slate-50"
          >
            <GoogleIcon />
            Continue with Google
          </button>
          <p className="mt-4 text-center text-[11px] text-slate-500">
            Only allowlisted Google Workspace emails can access this console.
          </p>
        </div>
      </div>
    );
  }

  const rows = tab === "demos" ? demos : tab === "affiliates" ? affiliates : optouts;

  return (
    <div className="min-h-screen bg-slate-50/60">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-slate-500 hover:text-[#1e1b4b]">← Back</Link>
            <Logo />
            <span className="hidden text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 sm:inline">· Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 sm:flex">
              {user.picture && (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />
              )}
              <div className="text-xs">
                <div className="font-medium text-[#1e1b4b]">{user.name || user.email}</div>
                <div className="text-slate-500">{user.email}</div>
              </div>
            </div>
            <button onClick={loadAll} disabled={busy} data-testid="admin-refresh-button" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total Demo Requests" value={stats?.demos ?? "—"} icon={Inbox} />
          <StatCard label="Total Affiliate Apps" value={stats?.affiliates ?? "—"} icon={HandCoins} />
          <StatCard label="Opt-Out Requests" value={stats?.optouts ?? "—"} icon={ShieldOff} />
          <StatCard label="Last 24h activity" value={(stats?.last24h_demos ?? 0) + (stats?.last24h_affiliates ?? 0)} accent />
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1">
            {[
              { id: "demos", label: "Demo Requests", icon: Inbox, n: demos.length },
              { id: "affiliates", label: "Affiliate Apps", icon: HandCoins, n: affiliates.length },
              { id: "optouts", label: "Opt-Outs", icon: ShieldOff, n: optouts.length },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setOpenRow(null); }}
                  data-testid={`admin-tab-${t.id}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${
                    active ? "bg-[#1e1b4b] text-white shadow-[0_6px_16px_-6px_rgba(30,27,75,0.5)]" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-medium">{t.label}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {t.n}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => downloadCsv(tab)}
            disabled={rows.length === 0}
            data-testid="admin-export-csv-button"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-[#1e1b4b] hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {rows.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-sm text-slate-500">{busy ? "Loading…" : "No submissions yet."}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 text-left font-semibold w-8" />
                  {tab === "optouts" ? (
                    <>
                      <th className="px-5 py-3 text-left font-semibold">Email</th>
                      <th className="px-5 py-3 text-left font-semibold">Reason</th>
                      <th className="px-5 py-3 text-left font-semibold">Status</th>
                    </>
                  ) : (
                    <>
                      <th className="px-5 py-3 text-left font-semibold">Name</th>
                      <th className="px-5 py-3 text-left font-semibold">Email</th>
                      <th className="px-5 py-3 text-left font-semibold">Company</th>
                      <th className="px-5 py-3 text-left font-semibold">{tab === "demos" ? "Interest" : "Clients"}</th>
                    </>
                  )}
                  <th className="px-5 py-3 text-right font-semibold">Received</th>
                  <th className="px-5 py-3 text-right font-semibold w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = openRow === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        onClick={() => setOpenRow(isOpen ? null : r.id)}
                        className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/60 ${isOpen ? "bg-slate-50/60" : ""}`}
                        data-testid={`admin-row-${r.id}`}
                      >
                        <td className="px-5 py-3 text-slate-400">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        {tab === "optouts" ? (
                          <>
                            <td className="px-5 py-3 font-medium text-[#1e1b4b]">
                              <a href={`mailto:${r.email}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{r.email}</a>
                            </td>
                            <td className="px-5 py-3 text-slate-600 truncate max-w-xs">{r.reason || "—"}</td>
                            <td className="px-5 py-3">
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                                {r.status || "pending"}
                              </span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-5 py-3 font-medium text-[#1e1b4b]">{r.name}</td>
                            <td className="px-5 py-3 text-slate-600">
                              <a href={`mailto:${r.email}`} className="hover:text-[#1e1b4b]" onClick={(e) => e.stopPropagation()}>{r.email}</a>
                            </td>
                            <td className="px-5 py-3 text-slate-700">{r.company}</td>
                            <td className="px-5 py-3 text-slate-600">{tab === "demos" ? (r.interest || "—") : (r.monthly_clients || "—")}</td>
                          </>
                        )}
                        <td className="px-5 py-3 text-right font-mono text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const label = tab === "optouts" ? r.email : `${r.name} (${r.company})`;
                              setConfirmDelete({ kind: tab, id: r.id, label });
                            }}
                            data-testid={`admin-delete-row-${r.id}`}
                            aria-label="Delete row"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/40">
                          <td colSpan={7} className="px-5 py-4">
                            <DetailGrid row={r} tab={tab} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Delete-confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setConfirmDelete(null)}
          data-testid="admin-delete-confirm-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-7 shadow-[0_30px_80px_-30px_rgba(30,27,75,0.4)]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-[#1e1b4b]">Delete this row?</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              You're about to permanently delete{" "}
              <span className="font-medium text-[#1e1b4b]">{confirmDelete.label}</span>{" "}
              from <strong>{confirmDelete.kind}</strong>. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                data-testid="admin-delete-cancel-button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                data-testid="admin-delete-confirm-button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-2xl border bg-white p-5 ${accent ? "border-[#d4af37]/40" : "border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && (
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#1e1b4b]/[0.06] text-[#1e1b4b]">
            <Icon className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
      <p className={`mt-2 text-3xl font-medium tracking-tight ${accent ? "text-[#7c5e0a]" : "text-[#1e1b4b]"}`}>
        {value}
      </p>
    </div>
  );
}

function DetailGrid({ row, tab }) {
  const items = tab === "demos"
    ? [["Role", row.role], ["Company Size", row.employees], ["Interest", row.interest], ["Notes", row.notes], ["ID", row.id]]
    : tab === "affiliates"
    ? [["Website", row.website], ["Active Clients", row.monthly_clients], ["Verticals", row.verticals], ["ID", row.id]]
    : [["Email", row.email], ["Reason", row.reason], ["Status", row.status], ["ID", row.id]];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          <p className="mt-1 break-words text-sm text-[#1e1b4b]">{value || <span className="text-slate-400">—</span>}</p>
        </div>
      ))}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
